import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import Alert from "../components/ui/Alert";
import { Input, PasswordInput } from "../components/ui/Input";
import AppBanner from "../components/AppBanner";
import { apiService } from "../utils/apiService";
import { useLoadingState } from "../hooks/useLoadingState";
import { useFormState } from "../hooks/useFormState";
import {
  validateRequiredFields,
  validatePassword,
} from "../utils/validationUtils";

const Register = () => {
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { loading, withLoading } = useLoadingState();

  const { formData, handleChange } = useFormState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate required fields
    const requiredValidation = validateRequiredFields(formData, [
      "email",
      "password",
      "firstName",
      "lastName",
    ]);
    if (!requiredValidation.isValid) {
      setError(requiredValidation.errors[0]);
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return;
    }

    try {
      await withLoading(async () => {
        return await apiService.post(
          "/users/register",
          {
            email: formData.email,
            password: formData.password,
            firstName: formData.firstName,
            lastName: formData.lastName,
          },
          { defaultErrorMessage: "Registration failed" },
        );
      });

      navigate("/login", {
        state: { message: "Registration successful! Please log in." },
      });
    } catch (err) {
      apiService.handleError(err, setError, "Registration failed");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AppBanner />
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-center text-3xl font-extrabold text-gray-900">
              Create an Account
            </h1>
            <p className="text-center text-sm text-gray-600 mt-2">
              Join NFL Picks&apos;Em
            </p>
          </div>

          <div className="bg-white shadow-md rounded-lg p-8">
            {error && (
              <Alert color="red" className="mb-4">
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                required
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  placeholder="John"
                  required
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                />
                <Input
                  label="Last Name"
                  placeholder="Doe"
                  required
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
              <PasswordInput
                label="Password"
                placeholder="Your password"
                required
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
              <Button fullWidth type="submit" loading={loading}>
                Register
              </Button>
            </form>

            <div className="text-center mt-6">
              <p className="text-sm text-gray-600">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
